import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeededitMultiStyleParams {
  /**
   * 输入图像URL，必须是有效的HTTP或HTTPS URL，支持格式：JPG、JPEG、PNG
   */
  image_input1: string;
  /**
   * 模板ID，可选值：23种风格模板（如felt_3d_polaroid、my_world等）
   * @values felt_3d_polaroid | my_world | my_world_universal | plastic_bubble_figure | plastic_bubble_figure_cartoon_text | furry_dream_doll | micro_landscape_mini_world | micro_landscape_mini_world_professional | acrylic_ornaments | felt_keychain | lofi_pixel_character_mini_card | angel_figurine | lying_in_fluffy_belly | glass_ball | earphone_case_style | electronic_pet_egg_style | patchwork_collage_style | claw_machine_style | car_miniature_ornaments | Mid-Autumn_Festival_new_chinese_style | Mid-Autumn_Festival_individual | Christmas_green_background | Christmas_tree | Spring_Festival_traditional_Chinese_architecture | birthday_photo_gorgeous | birthday_photo_red | birthday_photo_party
   */
  template_id: 'felt_3d_polaroid' | 'my_world' | 'my_world_universal' | 'plastic_bubble_figure' | 'plastic_bubble_figure_cartoon_text' | 'furry_dream_doll' | 'micro_landscape_mini_world' | 'micro_landscape_mini_world_professional' | 'acrylic_ornaments' | 'felt_keychain' | 'lofi_pixel_character_mini_card' | 'angel_figurine' | 'lying_in_fluffy_belly' | 'glass_ball' | 'earphone_case_style' | 'electronic_pet_egg_style' | 'patchwork_collage_style' | 'claw_machine_style' | 'car_miniature_ornaments' | 'Mid-Autumn_Festival_new_chinese_style' | 'Mid-Autumn_Festival_individual' | 'Christmas_green_background' | 'Christmas_tree' | 'Spring_Festival_traditional_Chinese_architecture' | 'birthday_photo_gorgeous' | 'birthday_photo_red' | 'birthday_photo_party';
  /**
   * 输出图像宽度，取值范围：512-2048，默认值为1328
   * @default 1328
   */
  width?: number;
  /**
   * 输出图像高度，取值范围：512-2048，默认值为1328
   * @default 1328
   */
  height?: number;
}

export interface VolcesSeededitMultiStyleResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seededit_multi_style
 *
 * Volces SeedEdit Multi Style Generator - 火山引擎SeedEdit多风格生成实现
 * 
 * Model: SeedEdit Multi Style
 * Features: 多风格图像生成
 * Input: 1张参考图像 + 模板ID
 * Output Dimensions: 512-2048像素（默认1328x1328）
 * Templates: 支持23种风格模板
 * 
 * API Documentation: https://www.volcengine.com/docs/86081/1804566
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeededitMultiStyle } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeededitMultiStyle({
 *   // 参数见 VolcesSeededitMultiStyleParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeededitMultiStyleParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeededitMultiStyle(
  params: VolcesSeededitMultiStyleParams,
): Promise<VolcesSeededitMultiStyleResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seededit_multi_style',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeededitMultiStyleResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
